/**
 * The Lit Pass page's way back to the game that sent the player here.
 *
 * A game opens the page in a NEW tab (`window.open` from its sandboxed frame —
 * the only way a plugin can reach a platform page), so the player arrives on a
 * page with nothing on it that leads back. Tom, 2026-10-16: "When I click on
 * get a LitPass it should be easy to return to the LitCraft game after I go
 * to that page." The game names itself in the URL —
 * `/a/arche_game_rewards/subscription?return=<its arche id>` (the retired
 * `/store` address redirects there with the query intact) — and the page
 * offers "Back to <its name>".
 *
 * The parameter is an ARCHE ID and nothing else: never a URL, never a path.
 * The page only ever builds `/a/<id>` from it, so it cannot be turned into a
 * redirect to anywhere else, and it is never shown as text — the button's
 * words are the arche's own registered name, read from the platform.
 */

/** Longest id accepted — far past any real one, short of anything abusive. */
const MAX_RETURN_ID = 64;

/**
 * The same two id shapes the bridge's `open_arche` accepts
 * (`isValidArcheIdFormat` in `arche/components/PluginBridge.ts`): a generated
 * plugin arche id, or a seeded first-party one.
 */
const RETURN_ARCHE_ID = /^(arc_[a-f0-9]{32}|arche_[a-z0-9_]+)$/;

/** The arche id `?return=` names, or null when it is absent or not an arche id. */
export function parseReturnArcheId(search: string): string | null {
  const value = new URLSearchParams(search).get('return');
  if (!value || value.length > MAX_RETURN_ID) return null;
  return RETURN_ARCHE_ID.test(value) ? value : null;
}

/** How long a `window.close()` is given before the tab is taken back itself. */
export const CLOSE_GRACE_MS = 300;

export interface ReturnSeams {
  /** Entries in this tab's history: 1 means a tab opened just for this page. */
  historyLength: () => number;
  close: () => void;
  isClosed: () => boolean;
  /** Go to the arche's page in this tab. */
  go: (path: string) => void;
  setTimeout: (fn: () => void, ms: number) => unknown;
}

/**
 * Back to the arche. A tab the game opened just for this page (one history
 * entry — `window.open` made it) is closed, so the player lands on the game
 * still running in the tab they came from, exactly where they left it. A
 * browser refuses `close()` for a tab a script did not open, and nothing says
 * so — hence the check a moment later: still here, then this tab goes to the
 * arche's page instead. A tab with history of its own is never closed.
 */
export function returnToArche(archeId: string, seams: ReturnSeams): void {
  const path = `/a/${archeId}`;
  if (seams.historyLength() > 1) {
    seams.go(path);
    return;
  }
  seams.close();
  seams.setTimeout(() => {
    if (!seams.isClosed()) seams.go(path);
  }, CLOSE_GRACE_MS);
}
